/**
 * @file Registration.jsx
 * @description Manages new user setup and profile creation
 *
 * @section Responsibilities
 * - Collect user information
 * - Handle document uploads
 * - Validate profile data
 * - Track onboarding progress
 *
 * @section Dependencies
 * - Profile Management: User data handling
 * - Document Service: File handling
 * - Validation Service: Data validation
 *
 * @section Integration Points
 * - Profile API: Data submission
 * - Document Service: File handling
 * - Onboarding Flow: Progress tracking
 *
 * @section State Management
 * - profileData: User information
 * - validationState: Form validation
 * - progress: Onboarding progress
 *
 * @section Events
 * - onProfileUpdate: Profile changes
 * - onDocumentUpload: File handling
 * - onValidation: Form validation
 *
 * @section Props
 * @prop {Object} initialData - Initial profile data
 * @prop {Function} onComplete - Completion callback
 */

import React, { useEffect, useRef, useState } from "react";
import Steps from "../../components/onboarding/Steps";
import { Grid } from "@mui/material";
import TextInput from "../../components/forms/TextInput";
import SelectInput from "../../components/forms/SelectInput";
import AccountForm from "./AccountForm";
import DgButton from "../../components/DgButton";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import { useApplicationContext } from "../../contexts/ApplicationContext";
import Request from "../../utils/fetchUtils";
import Swal from "sweetalert2";

const newAccountInitial = {
  name: "",
  email: "",
  repCode: "ADF", //
  options: true, //  ??
  applicationType: {
    optionAgreementForm: {
      riskStandardOptions: "2024-08-21",
      optionApproval: 2,
      uncoveredOptionWriters: "2024-08-21",
    },
    type: "",
    newAccountForm: {
      tradeAuthorization: {
        isTradeAuthorization: "YES",
      },
      accountType: "MARGIN",
      customerType: "INDIVIDUAL",
      suitabilityProfile: {
        timeHorizon: "AVERAGE",
        liquidityNeeds: "NOT_IMPORTANT",
      },
      investmentProfile: {
        totalNetWorthUSD: {
          min: 200001,
          max: 500000,
        },
        liquidNetWorthUSD: {
          min: 0,
          max: 50000,
        },
        annualIncomeUSD: {
          min: 100001,
          max: 200000,
        },
        stockExperience: 1,
        optionExperience: 1,
        investmentObjective: "GROWTH",
        riskTolerance: "MEDIUM",
        investmentExperience: "LIMITED",
      },
      applicants: [
        {
          identity: {
            name: {
              givenName: "",
              familyName: "",
            },
            dateOfBirth: "1988-05-27", // FORMAT
            citizenshipCountry: "USA",
            socialSecurityNumber: "629-01-2327", // FORMAT
          },
          contact: {
            homeAddress: {
              streetAddress: ["39642 Rickard Rd"],
              country: "USA",
              city: "Lovettsville",
              state: "VA",
              postalCode: "20180",
            },
            phoneNumbers: [
              {
                phoneNumber: "703-554-7112", // FORMAT
                phoneNumberType: "HOME",
              },
            ],
            emailAddresses: ["michael@aureum.ai"],
          },
          disclosures: {
            isPoliticallyExposed: "NO",
            isControlPerson: "NO",
            isAffiliatedExchangeOrFINRA: "NO",
          },
          employment: {
            employmentStatus: "SELF_EMPLOYED",
            employer: "Aureum AI",
            businessAddress: {
              streetAddress: ["39642 Rickard Rd"],
              city: "Lovettsville",
              state: "VA",
              postalCode: "20180",
              country: "USA",
            },
          },
          maritalStatus: "MARRIED",
        },
      ],
    },
  },
};

const TYPES = Object.freeze({
  rollover_ira: "ROLLOVER_ACCOUNT",
  roth_ira: "ROTH_IRA",
  traditional_ira: "TRADITIONAL_IRA",
});

const CUSTOMER_TYPES = Object.freeze({
  rollover_ira: "IRA",
  roth_ira: "IRA",
  traditional_ira: "IRA",
  joint_margin_survivor: "JOINT",
  joint_cash_survivor: "JOINT",
  individual_margin: "INDIVIDUAL",
  individual_cash: "INDIVIDUAL",
});

function Registration() {
  const navigate = useNavigate();
  const { applicationType, setApplicationType } = useApplicationContext();

  const [accountForm, setAccountForm] = useState({});
  const accountFormRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [step1Errors, setStep1Errors] = useState({});
  const [backendFieldErrors, setBackendFieldErrors] = useState({});

  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}`;
  const sessionFullName = localStorage.getItem("fullName");
  const sessionEmail = localStorage.getItem("email");

  const emptyApplication = {
    name: sessionFullName,
    email: sessionEmail,
    //email: 'george@whitehouse.gov',
    repCode: "ADF",
    options: true,
    applicationType: {
      optionAgreementForm: {
        riskStandardOptions: formattedDate,
        optionApproval: 2,
        uncoveredOptionWriters: formattedDate,
      },
      type: "",
    },
  };

  const [applicationData, setApplicationData] = useState(emptyApplication);

  const [isUsaCitizen, setIsUsaCitizen] = useState("");
  const [type, setType] = useState("");
  const [accountType, setAccountType] = useState("");

  const handleCitizenship = (value) => {
    const booleanValue = type === "individual_margin" ? "" : value === "true";
    setIsUsaCitizen(booleanValue);
  };

  const handleChange = (field, value) => {
    setApplicationData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleTypeChange = (newType) => {
    const accountType = newType.includes("cash")
      ? "CASH"
      : newType.includes("margin")
        ? "MARGIN"
        : "IRA";
    setAccountType(accountType);
    setType(newType);
    setApplicationData((prevData) => ({
      ...prevData,
      applicationType: {
        ...prevData.applicationType,
        type: newType,
      },
    }));

    // context
    setApplicationType(newType);

    if (newType == "individual_margin" && !isUsaCitizen) {
      setIsUsaCitizen("");
    }
  };

  const handleAccountData = (data) => {
    setAccountForm(data);
    accountFormRef.current = data;

    const applicantName = data?.applicants?.[0]?.identity?.name;
    if (applicantName?.givenName || applicantName?.familyName) {
      const syncedName = [applicantName.givenName, applicantName.familyName]
        .filter(Boolean)
        .join(" ");
      setApplicationData((prevState) => ({ ...prevState, name: syncedName }));
    }

    if (
      applicationType === "rollover_ira" ||
      applicationType === "roth_ira" ||
      applicationType === "traditional_ira"
    ) {
      setApplicationData((prevState) => ({
        ...prevState,
        applicationType: {
          ...prevState.applicationType,
          newAccountForm: undefined,
          newAccountIraForm: {
            ...data,
            customerType: CUSTOMER_TYPES[applicationType] || "",
            accountType: "CASH",
            retirementAccountBeneficiariesForm: undefined,
            tradeAuthorization: {
              ...data.tradeAuthorization,
              agentName: "Tradier",
            },
            suitabilityProfile: {
              ...data.suitabilityProfile,
              liquidityNeeds: undefined,
            },
            applicants: [
              {
                ...data.applicants?.[0],
                identity: {
                  ...data.applicants?.[0]?.identity,
                  numDependents: undefined,
                },
                numDependents: data.applicants?.[0]?.identity?.numDependents,
              },
            ],
          },
          retirementAccountBeneficiariesForm: {
            ...data.retirementAccountBeneficiariesForm,
          },
          iraSepRolloverAgreementForm: {
            typeIRA: `${TYPES[applicationType]}`,
          },
        },
      }));
    }

    if (
      applicationType === "individual_cash" ||
      applicationType === "individual_margin"
    ) {
      setApplicationData((prevState) => ({
        ...prevState,
        applicationType: {
          ...prevState.applicationType,
          newAccountIraForm: undefined,
          newAccountForm: {
            ...data,
            customerType: CUSTOMER_TYPES[applicationType] || "",
          },
        },
      }));
    }

    if (
      applicationType === "joint_cash_survivor" ||
      applicationType === "joint_margin_survivor"
    ) {
      setApplicationData((prevState) => ({
        ...prevState,
        applicationType: {
          ...prevState.applicationType,
          newAccountIraForm: undefined,
          jointRightsOfSurvivorshipForm: {},
          newAccountForm: {
            ...data,
            customerType: CUSTOMER_TYPES[applicationType] || "",
            retirementAccountBeneficiariesForm: undefined,
          },
        },
      }));
    }
  };

  const handleNestedChange = (field, subField, value) => {
    setApplicationData((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        [subField]: value,
      },
    }));
  };

  const getApplicationTypes = () => [
    {
      text: "Individual (Cash)",
      value: "individual_cash",
    },
    {
      text: "Individual (Margin)",
      value: "individual_margin",
      disabled: !isUsaCitizen,
    },
    {
      text: "Joint Survivor (Cash)",
      value: "joint_cash_survivor",
    },
    {
      text: "Joint Survivor (Margin)",
      value: "joint_margin_survivor",
      disabled: !isUsaCitizen,
    },
    {
      text: "Rollover IRA",
      value: "rollover_ira",
      disabled: !isUsaCitizen,
    },
    {
      text: "Traditional IRA",
      value: "traditional_ira",
      disabled: !isUsaCitizen,
    },
    {
      text: "Roth IRA",
      value: "roth_ira",
      disabled: !isUsaCitizen,
    },
  ];

  const validateStep1 = () => {
    const errors = {};
    if (!applicationData.name?.trim()) {
      errors.name = "Full name is required";
    }
    if (isUsaCitizen === "") {
      errors.isUsaCitizen = "Please indicate your citizenship status";
    }
    if (!type) {
      errors.type = "Please select an application type";
    }
    return errors;
  };


  const buildPayload = () => {
    const af = accountFormRef.current;
    const applicantName = af?.applicants?.[0]?.identity?.name;
    const syncedName =
      applicantName?.givenName || applicantName?.familyName
        ? [applicantName.givenName, applicantName.familyName]
            .filter(Boolean)
            .join(" ")
        : applicationData.name;

    const accountFormData = {
      ...af,
      customerType: CUSTOMER_TYPES[applicationType] || "",
    };

    let applicationType_ = { ...applicationData.applicationType };

    if (
      applicationType === "individual_cash" ||
      applicationType === "individual_margin"
    ) {
      applicationType_ = {
        ...applicationType_,
        newAccountIraForm: undefined,
        newAccountForm: {
          ...accountFormData,
          customerType: CUSTOMER_TYPES[applicationType] || "",
        },
      };
    } else if (
      applicationType === "joint_cash_survivor" ||
      applicationType === "joint_margin_survivor"
    ) {
      applicationType_ = {
        ...applicationType_,
        newAccountIraForm: undefined,
        jointRightsOfSurvivorshipForm: {},
        newAccountForm: {
          ...accountFormData,
          customerType: CUSTOMER_TYPES[applicationType] || "",
          retirementAccountBeneficiariesForm: undefined,
        },
      };
    } else if (
      applicationType === "rollover_ira" ||
      applicationType === "roth_ira" ||
      applicationType === "traditional_ira"
    ) {
      applicationType_ = {
        ...applicationType_,
        newAccountForm: undefined,
        newAccountIraForm: {
          ...accountFormData,
          customerType: CUSTOMER_TYPES[applicationType] || "",
          accountType: "CASH",
          retirementAccountBeneficiariesForm: undefined,
          tradeAuthorization: {
            ...af.tradeAuthorization,
            agentName: "Tradier",
          },
          suitabilityProfile: {
            ...af.suitabilityProfile,
            liquidityNeeds: undefined,
          },
          applicants: [
            {
              ...af.applicants?.[0],
              identity: {
                ...af.applicants?.[0]?.identity,
                numDependents: undefined,
              },
              numDependents: af.applicants?.[0]?.identity?.numDependents,
            },
          ],
        },
        retirementAccountBeneficiariesForm: {
          ...af.retirementAccountBeneficiariesForm,
        },
        iraSepRolloverAgreementForm: {
          typeIRA: `${TYPES[applicationType]}`,
        },
      };
    }

    return {
      ...applicationData,
      name: syncedName,
      applicationType: applicationType_,
    };
  };

  const validatePayload = (payload) => {
    const allErrors = [];
    const form =
      payload.applicationType?.newAccountForm ||
      payload.applicationType?.newAccountIraForm ||
      {};

    // Suitability
    const sp = form.suitabilityProfile || {};
    if (!sp.timeHorizon) allErrors.push("Time Horizon is required");
    if (!sp.liquidityNeeds && applicationType !== "rollover_ira" && applicationType !== "roth_ira" && applicationType !== "traditional_ira") {
      allErrors.push("Liquidity Needs is required");
    }

    // Investment profile
    const ip = form.investmentProfile || {};
    if (!ip.totalNetWorthUSD?.max) allErrors.push("Total Net Worth is required");
    if (!ip.liquidNetWorthUSD?.max) allErrors.push("Liquid Net Worth is required");
    if (!ip.annualIncomeUSD?.max) allErrors.push("Annual Income is required");
    if (!ip.riskTolerance) allErrors.push("Risk Tolerance is required");
    if (!ip.investmentExperience) allErrors.push("Investment Experience is required");
    if (ip.stockExperience === "" || ip.stockExperience == null) allErrors.push("Stock Trading Experience is required");
    if (ip.optionExperience === "" || ip.optionExperience == null) allErrors.push("Option Trading Experience is required");

    // Applicant
    const applicant = form.applicants?.[0] || {};
    const identity = applicant.identity || {};
    const givenName = identity.name?.givenName?.trim() || "";
    const familyName = identity.name?.familyName?.trim() || "";

    if (!givenName) allErrors.push("First Name is required");
    else if (givenName.length > 20) allErrors.push("First Name must be 20 characters or less");
    if (!familyName) allErrors.push("Last Name is required");
    else if (familyName.length > 20) allErrors.push("Last Name must be 20 characters or less");
    if (!identity.dateOfBirth) allErrors.push("Date of Birth is required");
    const ssnRegex = /^[0-9]{3}-[0-9]{2}-[0-9]{4}$/;
    if (!identity.socialSecurityNumber?.trim()) {
      allErrors.push("Social Security Number is required");
    } else if (!ssnRegex.test(identity.socialSecurityNumber)) {
      allErrors.push("SSN must be in format 123-45-6789");
    }
    if (!applicant.maritalStatus) allErrors.push("Marital Status is required");

    const addr = applicant.contact?.homeAddress || {};
    if (!addr.state) allErrors.push("Home State is required");
    if (!addr.city?.trim()) allErrors.push("Home City is required");
    if (!addr.streetAddress?.[0]?.trim()) allErrors.push("Home Street Address is required");
    if (!addr.postalCode?.trim()) allErrors.push("Home Postal Code is required");

    const emp = applicant.employment || {};
    if (!emp.employmentStatus) allErrors.push("Employment Status is required");
    if (emp.employmentStatus === "EMPLOYED" && !emp.employer?.trim()) {
      allErrors.push("Employer name is required when employed");
    }
    const bAddr = emp.businessAddress || {};
    if (!bAddr.city?.trim()) allErrors.push("Business City is required");
    if (!bAddr.state) allErrors.push("Business State is required");
    if (!bAddr.streetAddress?.[0]?.trim()) allErrors.push("Business Street Address is required");
    if (!bAddr.postalCode?.trim()) allErrors.push("Business Postal Code is required");

    return allErrors;
  };

  const sendApplication = async () => {
    setSubmitted(true);
    const s1Errors = validateStep1();
    setStep1Errors(s1Errors);

    const payload = buildPayload();
    const allErrors = validatePayload(payload);

    if (allErrors.length > 0) {
      setTimeout(() => {
        const firstError = document.querySelector(".field-error-msg, .input-error");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 50);
      return;
    }

    setLoading(true);

    const res = await Request(
      `/trading/application`,
      payload,
      "POST",
    );
    setLoading(false);
    if (res.ok) {
      Swal.fire(
        "Application successfully sent",
        "Lets go to the next step",
        "success",
      ).then(() => {
        navigate(`/onboarding/agreements/${res.data.id}`);
      });
    } else {
      const rawErrors = res.data?.body?.error?.errors || res.body?.error?.errors || res.errors || [];

      if (rawErrors.length > 0) {
        // Map backend field paths to our field keys
        const fieldErrorMap = {
          "identity.name.givenName": "givenName",
          "identity.name.familyName": "familyName",
          "identity.dateOfBirth": "dateOfBirth",
          "identity.socialSecurityNumber": "socialSecurityNumber",
          "contact.homeAddress.city": "homeCity",
          "contact.homeAddress.state": "homeState",
          "contact.homeAddress.streetAddress": "homeStreetAddress",
          "contact.homeAddress.postalCode": "homePostalCode",
          "employment.businessAddress.city": "businessCity",
          "employment.businessAddress.state": "businessState",
          "employment.businessAddress.streetAddress": "businessStreetAddress",
          "employment.businessAddress.postalCode": "businessPostalCode",
          "employment.employerPresent": "employer",
          "maritalStatus": "maritalStatus",
        };

        const parsed = {};
        const unmapped = [];

        rawErrors.forEach((e) => {
          let matched = false;
          for (const [path, key] of Object.entries(fieldErrorMap)) {
            if (e.includes(path)) {
              if (e.includes("must not be empty")) parsed[key] = "This field is required";
              else if (e.includes("size must be between 1 and 20")) parsed[key] = "Must be 20 characters or less";
              else if (e.includes("employer is required")) parsed["employer"] = "Employer is required when employed";
              else parsed[key] = "Invalid value";
              matched = true;
              break;
            }
          }
          if (!matched) unmapped.push(e);
        });

        setBackendFieldErrors(parsed);
        setSubmitted(true);

        setTimeout(() => {
          const firstError = document.querySelector(".field-error-msg, .input-error");
          if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 50);

        if (unmapped.length > 0) {
          const errorsList = unmapped.map((e) => `<li style="text-align:left">${e}</li>`).join("");
          Swal.fire({
            title: "Please fix the following errors",
            html: `<ul style="padding-left:20px;margin-top:8px">${errorsList}</ul>`,
            icon: "error",
          });
        }
      } else {
        Swal.fire(
          res.message || "Submission error",
          "Please check that all required fields are filled correctly",
          "error",
        );
      }
    }
  };

  return (
    <div className="your-profile">
      <Steps>
        <p className="subtitle">Applicant Information</p>
        <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Full name",
                  name: "name",
                  value: applicationData.name,
                  action: (e) => {
                    handleChange("name", e.target.value);
                    if (submitted) setStep1Errors((prev) => ({ ...prev, name: e.target.value.trim() ? undefined : "Full name is required" }));
                  },
                  tooltipBool: false,
                  required: true,
                  error: step1Errors.name,
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Email",
                  name: "email",
                  value: applicationData.email,
                  action: (e) => handleChange("email", e.target.value),
                  tooltipBool: false,
                  disabled: true,
                }}
              />
            </div>
          </Grid>
        </Grid>
        <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <SelectInput
                customInput={{
                  label: "Are you a citizen of the United States?",
                  name: "isUsaCitizen",
                  value: isUsaCitizen,
                  action: (e) => {
                    handleCitizenship(e.target.value);
                    if (submitted) setStep1Errors((prev) => ({ ...prev, isUsaCitizen: undefined }));
                  },
                  tooltipBool: false,
                  required: true,
                  error: step1Errors.isUsaCitizen,
                }}
                data={[
                  {
                    text: "YES",
                    value: "true",
                  },
                  {
                    text: "NO",
                    value: "false",
                  },
                ]}
              ></SelectInput>
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <SelectInput
                customInput={{
                  label: "Application Type",
                  name: "type",
                  value: type,
                  action: (e) => {
                    handleTypeChange(e.target.value);
                    if (submitted) setStep1Errors((prev) => ({ ...prev, type: undefined }));
                  },
                  tooltipBool: false,
                  disabled: isUsaCitizen === "",
                  required: true,
                  error: step1Errors.type,
                }}
                data={getApplicationTypes()}
              />
            </div>
          </Grid>
        </Grid>
        <AccountForm
          actType={accountType}
          sendAccountData={handleAccountData}
          submitted={submitted}
          backendFieldErrors={backendFieldErrors}
        />
        <div className="button-container">
          <DgButton
            category={"primary"}
            size={"large"}
            text={"Next"}
            action={sendApplication}
            loading={loading}
          >
            <ArrowForwardIos className="icon next" />
          </DgButton>
        </div>
      </Steps>
    </div>
  );
}

export default Registration;
